import {
  createRestyleComponent,
  createVariant,
  color,
  border,
  spacing,
  ColorProps,
  BorderProps,
  SpacingProps,
  VariantProps,
  SpacingShorthandProps,
  LayoutProps,
} from '@shopify/restyle';
import { ComponentProps, ReactNode } from 'react';
import { TouchableOpacity } from 'react-native';

import { Text } from '@components/Restyle';
import { useHover } from '@hooks/useHover';
import theme, { Theme } from '@themes/theme';

type RestyleProps = ColorProps<Theme> &
  BorderProps<Theme> &
  SpacingProps<Theme> &
  SpacingShorthandProps<Theme> &
  VariantProps<Theme, 'badgeVariants'> &
  LayoutProps<Theme>;

const RestyleBadge = createRestyleComponent<
  RestyleProps & { children: ReactNode },
  Theme
>([color, border, spacing, createVariant({ themeKey: 'badgeVariants' })]);

type Props = Omit<ComponentProps<typeof RestyleBadge>, 'children'> & {
  label: string;
  activeOpacity?: number;
  onPress?: () => void;
  /** Opt-in hover tint (TA-9005, web only — hover is a no-op on native).
   * E.g. the Create Task priority chips rest on grey01 and pass grey02. */
  hoverBackgroundColor?: keyof Theme['colors'];
};

export const Badge: React.FC<Props> = ({
  label,
  activeOpacity = 0.2,
  onPress,
  hoverBackgroundColor,
  ...rest
}: Props) => {
  const { isHovered, hoverProps } = useHover({
    disabled: !hoverBackgroundColor || !onPress,
  });
  let textColor: ComponentProps<typeof Text>['color'];

  switch (rest.variant) {
    case 'red':
      textColor = 'alertRed';
      break;

    case 'yellow':
      textColor = 'yellowVivid';
      break;

    case 'green':
      textColor = 'greenDark';
      break;

    default:
      textColor = 'textSecondary';
  }

  return (
    <TouchableOpacity
      {...hoverProps}
      activeOpacity={activeOpacity}
      onPress={onPress}
      accessibilityLabel={label}>
      <RestyleBadge
        {...rest}
        {...({
          style:
            isHovered && hoverBackgroundColor
              ? { backgroundColor: theme.colors[hoverBackgroundColor] }
              : undefined,
        } as object)}
        alignSelf='flex-start'>
        <Text
          variant='labelSmall'
          color={textColor}
          textAlign='center'
          style={!!rest.width && { flex: 1 }}>
          {label}
        </Text>
      </RestyleBadge>
    </TouchableOpacity>
  );
};
